
import {
  defineComponent, onMounted,
  PropType, Ref,
  ref,
  watch,
  onBeforeUnmount,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';

import {
  TabItem,
  TabSubitem,
  ParserObject,
  TemplateStructureEnum,
} from '@/types';

import Tooltip from 'primevue/tooltip';

import ConfigurationPanelTabContentItem from '@/components/template-builder/panels/ConfigurationPanelTabContentItem.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';

export default defineComponent({
  name: 'ConfigurationPanelTabContent',

  components: {
    ConfigurationPanelTabContentItem,
    SpmButton,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },

    structureActions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ['refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const activeFieldGroupIndex: Ref<number[]> = ref([]);
    const toggleFieldGroup = (index: number) => {
      if (activeFieldGroupIndex.value.includes(index)) {
        activeFieldGroupIndex.value.splice(activeFieldGroupIndex.value.indexOf(index), 1);
      } else {
        activeFieldGroupIndex.value.push(index);
      }
    };
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);

    const tabItems = computed(() => {
      if (props.tabItem.tabPanel && props.tabItem.tabPanel.items) {
        if (selectedSyncElement.value && selectedSyncElement.value.elementPartType) {
          return props.tabItem.tabPanel.items.filter((item: any) => item.elementPartType === selectedSyncElement.value.elementPartType);
        }
        return props.tabItem.tabPanel.items;
      }
      return [];
    });

    const handleRefresh = () => {
      emit('refresh');
    };

    const stopWatchers: any[] = [];

    const initActiveFieldGroupIndex = (items: any[]) => {
      activeFieldGroupIndex.value = items.reduce((acc: number[], item, index) => {
        if (index === 0) {
          acc.push(index);
        }
        return acc;
      }, []);
    };

    stopWatchers.push(watch([
      () => props.parserValues,
      () => props.tabItem,
    ],
    () => {
      if (props.tabItem.tabPanel) {
        activeFieldGroupIndex.value = props.tabItem.tabPanel.items.reduce((acc: number[], item, index) => {
          acc.push(index);
          return acc;
        }, []);
      }
    }));

    stopWatchers.push(watch(selectedSyncElement, (newValue, oldValue) => {
      if (props.tabItem.tabPanel && !oldValue) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    }, { deep: true }));

    const enterEditSyncElement = () => {
      const elementType = TemplateStructureEnum.DESIGN;
      const name = props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    onMounted(() => {
      if (props.tabItem.tabPanel) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    });

    onBeforeUnmount(() => {
      stopWatchers.forEach((stop) => stop());
    });

    return {
      activeFieldGroupIndex,
      tabItems,
      toggleFieldGroup,
      handleRefresh,
      t,
      enterEditSyncElement,
      selectedSyncElement,
    };
  },
});
